// ---- CHARACTER PANEL (0.25 meta-progression) ----
// A full-screen overlay (built on the journalMenu template): your profile + equipped relics + lifetime
// stats. Tabs: LOADOUT (equip relics into account-level-gated slots), RELICS (collection codex), STATS
// (account level/XP + lifetime sheet). Opened with C / a touch button / the menu chip (see input/touch-hud).
import { S } from './state.js';
import { ctx } from './canvas.js';
import { roundRect } from './screens.js';
import { fitText } from './ui.js';
import { aim, pointIn } from './input.js';
import { RELICS, RELIC_TIERS, RELIC_SETS, RELIC_BY_KEY } from './data/relics.js';
import { accountProgress, relicSlots, nextSlotLevel } from './data/progression.js';
import { persist } from './economy.js';
import { recomputeStats } from './stats.js';

const TAB_DATA = [['loadout', '⚙ LOADOUT'], ['relics', '✦ RELICS'], ['stats', '📊 STATS']];
const STAR_COL = '#ffd66e';
function relStars(lvl) { return '★'.repeat(Math.max(1, Math.min(3, lvl || 1))); }   // 0.26 fusion: ★/★★/★★★
function fmtDur(sec) { const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60); return h > 0 ? h + 'h ' + m + 'm' : m + 'm ' + Math.floor(sec % 60) + 's'; }

// ---- loadout mutation (dense array of equipped keys, length ≤ slots) ----
function equipInto(slot, key) {
  const lo = S.save.relicLoadout;
  const at = lo.indexOf(key); if (at >= 0) lo.splice(at, 1);   // a relic lives in one slot only
  if (slot >= lo.length) lo.push(key); else lo[slot] = key;
  S.charPickSlot = null; recomputeStats(); persist();
}
function unequip(slot) { S.save.relicLoadout.splice(slot, 1); S.charPickSlot = null; recomputeStats(); persist(); }

export function characterPanel() {
  S.uiButtons = [];
  ctx.fillStyle = 'rgba(6,10,24,.93)'; ctx.fillRect(0, 0, S.W, S.H);
  const pw = Math.min(760, S.W - 24), ph = Math.min(640, S.H - 24);
  const px = (S.W - pw) / 2, py = (S.H - ph) / 2;
  ctx.fillStyle = 'rgba(12,18,38,.97)'; roundRect(px, py, pw, ph, 20); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.stroke();

  const xp = S.save.accountXp || 0, prog = accountProgress(xp), lvl = prog.level, slots = relicSlots(lvl);
  const owned = S.save.relicsOwned || {}, ownedN = Object.keys(owned).length;

  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#fff'; ctx.font = 'bold 23px system-ui';
  ctx.fillText('☷ CHARACTER', px + 24, py + 38);
  ctx.fillStyle = '#ffd66e'; ctx.font = 'bold 15px system-ui';
  ctx.fillText('Account Lv ' + lvl, px + 196, py + 38);

  // tabs — own row on narrow (phone) panels, like the journal
  const tabsOwnRow = pw < 620, tOff = tabsOwnRow ? 36 : 0;
  if (tabsOwnRow) {
    const tGap = 6, tw = (pw - 36 - tGap * 2) / 3, tyy = py + 54, th = 30; let _tx = px + 18;
    for (const [k, label] of TAB_DATA) { drawTab(k, label, _tx, tyy, tw, th); _tx += tw + tGap; }
  } else {
    let _tx = px + 330;
    for (const [k, label] of TAB_DATA) { const tw = 110, tyy = py + 20, th = 30; drawTab(k, label, _tx, tyy, tw, th); _tx += tw + 8; }
  }
  { const cs = 30, cxx = px + pw - cs - 16, cyy = py + 20;   // ✕ close
    ctx.fillStyle = 'rgba(255,255,255,.08)'; roundRect(cxx, cyy, cs, cs, 8); ctx.fill();
    ctx.fillStyle = '#9fb0e0'; ctx.font = 'bold 15px system-ui'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('✕', cxx + cs / 2, cyy + cs / 2 + 1);
    S.uiButtons.push({ x: cxx, y: cyy, w: cs, h: cs, action: () => { S.showCharacter = false; } });
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  }
  // subheader
  ctx.fillStyle = '#6b76a0'; ctx.font = '13px system-ui';
  ctx.fillText(S.charTab === 'loadout' ? slots + ' relic slot' + (slots === 1 ? '' : 's') + ' · tap a slot, then a relic to equip'
    : S.charTab === 'relics' ? 'collection — ' + ownedN + ' / ' + RELICS.length + ' relics found'
    : 'account & lifetime stats · C or Esc to close', px + 24, py + 60 + tOff);

  // clipped scroll viewport
  const vpX = px + 18, vpY = py + 78 + tOff, vpW = pw - 36, vpH = ph - 78 - tOff - 72;
  ctx.save(); roundRect(vpX, vpY, vpW, vpH, 12); ctx.clip();
  let cy0 = 8;   // each sub-fn owns its cursor + its rowY/vis (closing over ITS local cy0 — a shared closure here would never advance as the sub-fn mutates its own copy)
  if (S.charTab === 'loadout') cy0 = drawLoadout(vpX, vpY, vpW, vpH, cy0, slots, owned, lvl);
  else if (S.charTab === 'relics') cy0 = drawRelics(vpX, vpY, vpW, vpH, cy0, owned);
  else cy0 = drawStats(vpX, vpY, vpW, vpH, cy0, prog, lvl, slots, ownedN);

  ctx.restore();
  // scrollbar
  S.charScrollMax = Math.max(0, cy0 - vpH);
  if (S.charScroll > S.charScrollMax) S.charScroll = S.charScrollMax;
  if (S.charScrollMax > 0) {
    const thumbH = Math.max(28, vpH * vpH / cy0), thumbY = vpY + (S.charScroll / S.charScrollMax) * (vpH - thumbH);
    ctx.fillStyle = 'rgba(255,255,255,.14)'; roundRect(vpX + vpW - 5, thumbY, 4, thumbH, 2); ctx.fill();
  }
  // footer close
  const bw = 200, bh = 46, bx = px + (pw - bw) / 2, by = py + ph - bh - 16, hot = pointIn(aim, bx, by, bw, bh);
  ctx.fillStyle = hot ? '#3aa0ff' : '#2b6fd6'; roundRect(bx, by, bw, bh, 12); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.font = 'bold 17px system-ui'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('◂ CLOSE', bx + bw / 2, by + bh / 2);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  S.uiButtons.push({ x: bx, y: by, w: bw, h: bh, action: () => { S.showCharacter = false; } });
}

function drawTab(k, label, x, y, w, h) {
  const active = S.charTab === k;
  ctx.fillStyle = active ? '#2b6fd6' : 'rgba(255,255,255,.06)'; roundRect(x, y, w, h, 8); ctx.fill();
  ctx.fillStyle = active ? '#fff' : '#9fb0e0'; ctx.font = 'bold 12px system-ui'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(fitText(label, w - 6), x + w / 2, y + h / 2 + 1);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  S.uiButtons.push({ x, y, w, h, action: () => { S.charTab = k; S.charScroll = 0; S.charPickSlot = null; } });
}

// ---- LOADOUT: slot boxes (up to 3, account-level-gated) + an owned-relic picker when a slot is selected ----
function drawLoadout(vpX, vpY, vpW, vpH, cy0, slots, owned, lvl) {
  const rowY = () => vpY - S.charScroll + cy0;   // closes over THIS fn's cy0 (advances as the loop increments it)
  const vis = (h) => { const y = rowY(); return y + h > vpY && y < vpY + vpH; };
  const lo = S.save.relicLoadout;
  const sw = Math.min(vpW - 16, 520), sh = 64, sx = vpX + (vpW - sw) / 2;
  for (let i = 0; i < 3; i++) {
    const y = rowY(), locked = i >= slots, key = lo[i], r = key && RELICS.find(x => x.key === key);
    if (vis(sh)) {
      const col = r ? ((RELIC_TIERS[r.tier] || {}).color || '#c9a3ff') : '#3a4566';
      ctx.fillStyle = locked ? 'rgba(255,255,255,.02)' : (S.charPickSlot === i ? 'rgba(120,160,255,.12)' : 'rgba(255,255,255,.05)');
      roundRect(sx, y, sw, sh, 10); ctx.fill();
      ctx.strokeStyle = locked ? 'rgba(255,255,255,.06)' : (r ? col : (S.charPickSlot === i ? '#7fb6ff' : 'rgba(255,255,255,.14)')); ctx.lineWidth = S.charPickSlot === i ? 2 : 1.2; ctx.stroke();
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      if (locked) {
        ctx.fillStyle = '#6b76a0'; ctx.font = 'bold 14px system-ui';
        ctx.fillText('🔒  Reach Account Lv ' + (nextSlotLevel(lvl) || '—'), sx + 18, y + sh / 2);
      } else if (r) {
        ctx.font = '30px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(r.icon, sx + 14, y + sh / 2 + 1);
        ctx.font = 'bold 15px system-ui'; ctx.fillStyle = col; ctx.fillText(fitText(r.name, sw - 150), sx + 54, y + 22);
        ctx.fillStyle = STAR_COL; ctx.font = 'bold 13px system-ui'; ctx.textAlign = 'right'; ctx.fillText(relStars(owned[r.key]), sx + sw - 44, y + 22); ctx.textAlign = 'left';
        ctx.font = '11px system-ui'; ctx.fillStyle = '#9fb0e0'; ctx.fillText(fitText(r.desc, sw - 150), sx + 54, y + 39);
        ctx.fillStyle = '#ff8a8a'; ctx.fillText(fitText(r.down, sw - 150), sx + 54, y + 53);
        const ux = sx + sw - 34, uy = y + sh / 2 - 13;   // ✕ unequip
        ctx.fillStyle = 'rgba(255,120,120,.16)'; roundRect(ux, uy, 26, 26, 7); ctx.fill();
        ctx.fillStyle = '#ff9a9a'; ctx.font = 'bold 14px system-ui'; ctx.textAlign = 'center';
        ctx.fillText('✕', ux + 13, y + sh / 2 + 1); ctx.textAlign = 'left';
        const si = i; S.uiButtons.push({ x: ux, y: uy, w: 26, h: 26, action: () => unequip(si) });
      } else {
        ctx.fillStyle = S.charPickSlot === i ? '#7fb6ff' : '#6b76a0'; ctx.font = 'bold 14px system-ui';
        ctx.fillText(S.charPickSlot === i ? '▾  choose a relic below' : '＋  empty slot — tap to equip', sx + 18, y + sh / 2);
      }
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      if (!locked) { const si = i; S.uiButtons.push({ x: sx, y, w: sw - 40, h: sh, action: () => { S.charPickSlot = S.charPickSlot === si ? null : si; } }); }
    }
    cy0 += sh + 10;
  }
  // ---- SET BONUS strip (0.26 combos): every set you've partly or fully assembled — teaches the synergy ----
  const eq = S.save.relicLoadout || [];
  const relevant = RELIC_SETS.filter(s => s.members.some(k => eq.indexOf(k) >= 0));
  if (relevant.length) {
    cy0 += 6;
    if (vis(16)) { ctx.fillStyle = '#9fb0e0'; ctx.font = 'bold 12px system-ui'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic'; ctx.fillText('SET BONUS', vpX + 8, rowY() + 11); }
    cy0 += 22;
    for (const s of relevant) {
      const active = s.members.every(k => eq.indexOf(k) >= 0), y = rowY(), h = 40;
      if (vis(h)) {
        ctx.fillStyle = active ? 'rgba(120,210,150,.10)' : 'rgba(255,255,255,.03)'; roundRect(sx, y, sw, h, 8); ctx.fill();
        ctx.strokeStyle = active ? 'rgba(120,220,150,.5)' : 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
        ctx.font = 'bold 13px system-ui'; ctx.fillStyle = active ? '#8fe6a8' : '#7c87ad';
        ctx.fillText(fitText((active ? '✦ ' : '◇ ') + s.name, sw - 16), sx + 12, y + 17);
        const missing = s.members.filter(k => eq.indexOf(k) < 0).map(k => (RELIC_BY_KEY[k] || {}).name || k);
        const sub = active ? s.desc : 'equip ' + missing.join(' + ') + ' to complete';
        ctx.font = '10px system-ui'; ctx.fillStyle = active ? '#bfe8cc' : '#6b76a0';
        ctx.fillText(fitText(sub, sw - 16), sx + 12, y + 32);
      }
      cy0 += h + 6;
    }
  }
  // picker: owned relics not already equipped
  if (S.charPickSlot != null) {
    cy0 += 4;
    const avail = RELICS.filter(r => owned[r.key] && S.save.relicLoadout.indexOf(r.key) < 0);
    if (vis(20)) { ctx.fillStyle = '#9fb0e0'; ctx.font = 'bold 12px system-ui'; ctx.textAlign = 'left'; ctx.fillText(avail.length ? 'YOUR RELICS' : 'no spare relics — find more from bosses', vpX + 8, rowY() + 10); }
    cy0 += 22;
    for (const r of avail) {
      const y = rowY(), h = 44;
      if (vis(h)) {
        const col = (RELIC_TIERS[r.tier] || {}).color || '#c9a3ff';
        ctx.fillStyle = 'rgba(255,255,255,.05)'; roundRect(sx, y, sw, h, 9); ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.12)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.font = '22px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(r.icon, sx + 12, y + h / 2 + 1);
        ctx.font = 'bold 13px system-ui'; ctx.fillStyle = col; ctx.fillText(fitText(r.name, sw - 230), sx + 44, y + h / 2 - 7);
        ctx.fillStyle = STAR_COL; ctx.font = 'bold 12px system-ui'; ctx.textAlign = 'right'; ctx.fillText(relStars(owned[r.key]), sx + sw - 12, y + h / 2 - 7); ctx.textAlign = 'left';
        ctx.font = '10px system-ui'; ctx.fillStyle = '#9fb0e0'; ctx.fillText(fitText(r.desc + '  ·  ' + r.down, sw - 60), sx + 44, y + h / 2 + 9);
        ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
        const rk = r.key, slot = S.charPickSlot; S.uiButtons.push({ x: sx, y, w: sw, h, action: () => equipInto(slot, rk) });
      }
      cy0 += h + 6;
    }
  }
  return cy0 + 8;
}

// ---- RELICS: the collection codex, grouped by tier; unowned = silhouette ----
function drawRelics(vpX, vpY, vpW, vpH, cy0, owned) {
  const cols = vpW < 380 ? 1 : 2, colW = (vpW - 16 - 10 * (cols - 1)) / cols, h = 58;
  for (const tier of ['exotic', 'rare', 'common']) {
    const list = RELICS.filter(r => r.tier === tier), ti = RELIC_TIERS[tier];
    const hdrY = vpY - S.charScroll + cy0;
    if (hdrY + 16 > vpY && hdrY < vpY + vpH) { ctx.fillStyle = ti.color; ctx.font = 'bold 12px system-ui'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic'; ctx.fillText(ti.label + ' (' + list.filter(r => owned[r.key]).length + '/' + list.length + ')', vpX + 8, hdrY + 12); }
    cy0 += 24;
    const gridTop = vpY - S.charScroll + cy0;
    for (let i = 0; i < list.length; i++) {
      const r = list[i], have = !!owned[r.key];
      const cx = vpX + 8 + (i % cols) * (colW + 10), yy = gridTop + Math.floor(i / cols) * (h + 8);
      if (yy + h <= vpY || yy >= vpY + vpH) continue;   // cull off-screen cards
      ctx.fillStyle = have ? 'rgba(255,255,255,.05)' : 'rgba(255,255,255,.02)'; roundRect(cx, yy, colW, h, 9); ctx.fill();
      ctx.strokeStyle = have ? ti.color : 'rgba(255,255,255,.06)'; ctx.lineWidth = 1; ctx.stroke();
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.font = '24px system-ui'; ctx.fillStyle = have ? '#fff' : '#3a4566'; ctx.fillText(have ? r.icon : '✦', cx + 12, yy + h / 2);
      ctx.textBaseline = 'alphabetic';
      if (have) {
        ctx.font = 'bold 13px system-ui'; ctx.fillStyle = ti.color; ctx.fillText(fitText(r.name, colW - 78), cx + 44, yy + 16);
        ctx.fillStyle = STAR_COL; ctx.font = 'bold 12px system-ui'; ctx.textAlign = 'right'; ctx.fillText(relStars(owned[r.key]), cx + colW - 10, yy + 16); ctx.textAlign = 'left';
        ctx.font = '10px system-ui'; ctx.fillStyle = '#9fb0e0'; ctx.fillText(fitText(r.desc, colW - 52), cx + 44, yy + 33);
        ctx.fillStyle = '#ff8a8a'; ctx.fillText(fitText(r.down, colW - 52), cx + 44, yy + 47);
      } else {
        ctx.font = 'bold 13px system-ui'; ctx.fillStyle = '#6b76a0'; ctx.fillText('???', cx + 44, yy + 22);
        ctx.font = '10px system-ui'; ctx.fillStyle = '#4a557a'; ctx.fillText('undiscovered — drops from bosses', cx + 44, yy + 40);
      }
      ctx.textAlign = 'left';
    }
    cy0 += Math.ceil(list.length / cols) * (h + 8) + 10;
  }
  return cy0;
}

// ---- STATS: account + lifetime sheet ----
function drawStats(vpX, vpY, vpW, vpH, cy0, prog, lvl, slots, ownedN) {
  const rowY = () => vpY - S.charScroll + cy0;   // closes over THIS fn's cy0 (advances per row)
  const vis = (h) => { const y = rowY(); return y + h > vpY && y < vpY + vpH; };
  const s = S.save;
  // XP bar
  if (vis(40)) {
    const y = rowY(), bw = vpW - 16, bx = vpX + 8;
    ctx.fillStyle = '#cdd6ff'; ctx.font = 'bold 14px system-ui'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    ctx.fillText('Account Level ' + lvl, bx, y + 12);
    ctx.fillStyle = '#6b76a0'; ctx.font = '11px system-ui'; ctx.textAlign = 'right';
    ctx.fillText(prog.into + ' / ' + prog.need + ' XP', bx + bw, y + 12); ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(0,0,0,.4)'; roundRect(bx, y + 18, bw, 10, 5); ctx.fill();
    ctx.fillStyle = '#ffd66e'; roundRect(bx, y + 18, bw * Math.max(0, Math.min(1, prog.into / prog.need)), 10, 5); ctx.fill();
  }
  cy0 += 44;
  const next = nextSlotLevel(lvl);
  const rows = [
    ['Relic slots', slots + (next ? '   (next at Lv ' + next + ')' : '   (all unlocked)')],
    ['Relics collected', ownedN + ' / ' + RELICS.length],
    ['—', ''],
    ['Runs played', '' + (s.runs || 0)],
    ['Best wave', '' + (s.best || 0)],
    ['Lifetime kills', '' + (s.kills || 0)],
    ['Lifetime coins', '' + (s.coinsTotal || 0)],
    ['Time survived', fmtDur(s.timeTotal || 0)],
    ['Last run XP', '+' + (S.lastRunXp || 0)],
  ];
  for (const [k, v] of rows) {
    if (vis(26)) {
      const y = rowY();
      if (k === '—') { ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(vpX + 8, y + 6); ctx.lineTo(vpX + vpW - 8, y + 6); ctx.stroke(); }
      else {
        ctx.fillStyle = 'rgba(255,255,255,.04)'; roundRect(vpX + 8, y, vpW - 16, 24, 6); ctx.fill();
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#9fb0e0'; ctx.font = '13px system-ui'; ctx.textAlign = 'left'; ctx.fillText(k, vpX + 18, y + 13);
        ctx.fillStyle = '#fff'; ctx.font = 'bold 13px system-ui'; ctx.textAlign = 'right'; ctx.fillText(v, vpX + vpW - 18, y + 13);
        ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      }
    }
    cy0 += k === '—' ? 14 : 30;
  }
  return cy0 + 8;
}
